import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { Modal, Giro } from "./Modal";
import { dibujarTarjeta, textoCompartir, type DatosTarjeta } from "../lib/tarjeta";
import { enlaceInvitacion } from "../lib/referido";
import { useT } from "../lib/i18n";

const ENLACE = "https://keellabs.app";

/**
 * Compartir el resultado de una piscina, por WhatsApp o Telegram.
 *
 * DOS CAMINOS, Y EL BUENO DEPENDE DEL APARATO:
 *
 *  · En móvil (y en cualquier navegador con `navigator.share` que acepte ficheros) se comparte la
 *    IMAGEN de verdad: sale el menú del sistema con WhatsApp, Telegram y lo que haya instalado, y la
 *    tarjeta va adjunta. Es un solo gesto y es lo que la gente espera.
 *
 *  · En escritorio, ni WhatsApp Web ni Telegram admiten adjuntar una imagen desde un enlace: solo
 *    aceptan texto. Así que ahí el flujo honesto es copiar la tarjeta y pegarla en el chat — los dos
 *    clientes pegan imágenes del portapapeles sin problema. Por eso «Copiar imagen» está a la vista
 *    y el modal lo explica en vez de dejar que alguien mande el texto solo y se pregunte dónde quedó
 *    la tarjeta.
 *
 * La imagen se genera en el navegador: no sale de aquí nada de este vault hasta que su dueño decide
 * publicarlo.
 */
export function Compartir({ datos, cerrar }: { datos: DatosTarjeta; cerrar: () => void }) {
  const t = useT();
  // EL ENLACE QUE SE COMPARTE ES EL DE INVITACION. Esta pantalla ya era el sitio por el que la gente
  // enseñaba Keellabs a alguien; mandar aqui la URL pelada era regalar la unica atribucion que
  // habria existido. No cambia nada de lo que ve quien recibe: entra igual y paga igual.
  const { address } = useAccount();
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  // LA IMAGEN SE PINTA CON UN DATA URL, no con `createObjectURL`. Un `blob:` hay que revocarlo, y
  // basta con que el efecto se relance —cosa que pasaba: `datos` era un objeto literal nuevo en cada
  // repintado del padre, que se repinta solo cada 30 s— para que la limpieza mate la URL que el
  // <img> acababa de recibir. Resultado el 2026-08-16: la tarjeta se generaba perfectamente y se
  // veía rota. El data URL no depende del ciclo de vida de nadie.
  const huella = JSON.stringify(datos);

  useEffect(() => {
    let vivo = true;
    setError(null);
    dibujarTarjeta(datos)
      .then(({ png, blob: b }) => {
        if (!vivo) return;
        setBlob(b);
        setUrl(png);
      })
      .catch((e) => vivo && setError(e instanceof Error ? e.message : String(e)));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huella]);

  const texto = textoCompartir(datos);
  const enlace = address ? enlaceInvitacion(address) : ENLACE;
  const fichero = blob
    ? new File([blob], `keellabs-${datos.par.replace("/", "-").toLowerCase()}.png`, { type: "image/png" })
    : null;
  const puedeNativo = !!fichero && typeof navigator.canShare === "function"
    && navigator.canShare({ files: [fichero] });

  async function compartirNativo() {
    if (!fichero) return;
    try {
      await navigator.share({ files: [fichero], text: texto, url: enlace });
    } catch (e) {
      // Cancelar no es un fallo: el usuario cerró el menú y ya está.
      if ((e as Error)?.name !== "AbortError") {
        setError(t("Tu aparato no dejó abrir el menú de compartir. Copia la imagen y pégala en el chat."));
      }
    }
  }

  async function copiar() {
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      setError(t("Tu navegador no me deja copiar imágenes. Usa «Descargar» y adjúntala a mano."));
    }
  }

  function descargar() {
    if (!blob) return;
    // Para descargar SÍ conviene el blob: un data URL de medio mega en el atributo `href` lo
    // rechazan algunos navegadores. Se revoca en cuanto la descarga arranca.
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `keellabs-${datos.par.replace("/", "-").toLowerCase()}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(u), 10_000);
  }

  const abrir = (u: string) => window.open(u, "_blank", "noopener,noreferrer");
  const whatsapp = () => abrir(`https://wa.me/?text=${encodeURIComponent(`${texto}${enlace}`)}`);
  const telegram = () =>
    abrir(`https://t.me/share/url?url=${encodeURIComponent(enlace)}&text=${encodeURIComponent(texto)}`);

  return (
    <Modal abierto cerrar={cerrar} titulo={t("Compartir tu resultado")} ancho={640}
      pie={
        <>
          <button className="btn" onClick={cerrar}>{t("Cerrar")}</button>
          <div className="pie-derecha">
            <button className="btn" onClick={descargar} disabled={!blob}>{t("Descargar")}</button>
            {puedeNativo ? (
              <button className="btn principal" onClick={compartirNativo}>{t("Compartir con la imagen")}</button>
            ) : (
              <>
                <button className="btn" onClick={copiar} disabled={!blob}>
                  {copiado ? t("Copiada ✓") : t("1 · Copiar imagen")}
                </button>
                <button className="btn wasap" onClick={whatsapp} disabled={!blob}>{t("2 · WhatsApp")}</button>
                <button className="btn tele" onClick={telegram} disabled={!blob}>{t("2 · Telegram")}</button>
              </>
            )}
          </div>
        </>
      }
    >
      <div className="vista-tarjeta">
        {url ? <img src={url} alt={t("tarjeta con tu resultado")} />
             : error ? <p className="detalle">{error}</p>
             : <Giro texto={t("Dibujando…")} />}
      </div>

      {puedeNativo ? (
        <p className="detalle">
          {t("Se abrirá el menú de tu móvil con ")}<strong>{t("la tarjeta ya adjunta")}</strong>{t(": elige WhatsApp, Telegram o lo que uses.")}
        </p>
      ) : (
        <p className="detalle">
          <strong>{t("Copia la imagen primero")}</strong>{t(" y pégala en el chat cuando se abra: desde el escritorio, WhatsApp y Telegram solo aceptan el texto por enlace, la imagen hay que pegarla.")}
        </p>
      )}
      <p className="detalle">
        {t("La tarjeta ")}<strong>{t("no lleva tu importe, ni la dirección del vault, ni tu wallet")}</strong>{t(". Solo el par, el porcentaje y el tiempo. Se dibuja en tu navegador: no se sube a ningún sitio hasta que tú la mandes.")}
      </p>
      {error && url && <p className="aviso error"><span className="detalle">{error}</span></p>}
    </Modal>
  );
}
